import type { MatchClient, TimelineEntry } from "./match-client";
import type { Dict, Locale } from "./i18n";
import { personaName, reasonText, resourceKindName } from "./i18n";
import { slotTheme } from "./theme";

/**
 * The "🧠 Why?" inspector.
 *
 * Reconstructs what the cricket could actually see at that tick. This is the
 * payoff of the information-asymmetry design: a viewer can confirm for
 * themselves that a cricket walked into a trap because the trap was genuinely
 * outside its vision radius, not because the AI was simply stupid.
 *
 * Shared by the live side timelines and the post-match battle timeline, which is
 * why it lives in its own file: the inspector is the one part of the UI that
 * must give *identical* answers in both, otherwise the live view and the replay
 * would disagree about the same moment.
 */
export function WhyPanel({
  entry,
  client,
  locale,
  d,
  onClose,
}: {
  entry: TimelineEntry;
  client: MatchClient;
  locale: Locale;
  d: Dict;
  onClose: () => void;
}) {
  const theme = slotTheme(entry.slot);
  const obs = client.observationAt(entry.tick, entry.slot);

  return (
    <div className="why">
      <div className="why-title" style={{ display: "flex", justifyContent: "space-between" }}>
        <span>
          {d.whyTitle} · {personaName(client.personaOf(entry.agentId), locale)}
        </span>
        <button className="ghost" style={{ padding: "1px 7px", fontSize: 10 }} onClick={onClose}>
          {d.close}
        </button>
      </div>

      {entry.reason ? (
        <div className="why-reason">“{reasonText(entry.reason, locale)}”</div>
      ) : null}

      {obs ? (
        <>
          <div className="why-row">
            <span>{d.position}</span>
            <span>
              [{Math.round(obs.self.position[0])}, {Math.round(obs.self.position[1])}]
            </span>
          </div>
          <div className="why-row">
            <span>{d.hpScore}</span>
            <span>
              {obs.self.hp}/{obs.self.maxHp} · ◆{obs.self.score} · Σ{obs.self.totalScore}
            </span>
          </div>
          <div className="why-row">
            <span>{d.enemy}</span>
            <span>
              {obs.opponent.visible && obs.opponent.position
                ? d.enemyVisible(
                    Math.round(obs.opponent.position[0]),
                    Math.round(obs.opponent.position[1]),
                    obs.opponent.distance?.toFixed(1) ?? "?",
                  )
                : obs.hasSeenOpponent
                  ? d.enemyStale
                  : d.enemyNeverSeen}
            </span>
          </div>
          <div className="why-row">
            <span>{d.resourcesInSight}</span>
            <span>{obs.resources.length}</span>
          </div>
          {obs.resources.length > 0 ? (
            <div style={{ marginTop: 6, borderTop: "1px solid var(--line)", paddingTop: 6 }}>
              {obs.resources.slice(0, 6).map((r) => (
                <div key={r.id} className="why-row">
                  <span>
                    {r.identifiedAsTrap
                      ? d.resourceAsTrap(r.id)
                      : d.resourcePlain(resourceKindName(r.kind, locale), r.id)}
                  </span>
                  <span>
                    {d.distanceTag(r.distance.toFixed(1))}
                    {r.inRange ? ` · ${d.inRange}` : ""}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : (
        <div className="hint">{d.observationUnavailable}</div>
      )}

      <div className="hint" style={{ marginTop: 6, color: theme.dim }}>
        {d.visionNote}
      </div>
    </div>
  );
}
